import React, { useEffect, useRef } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { canOnSale } from '../permissions/access';
import { markFirstTask } from '../onboarding/firstTaskRegistry';
import useCheckoutState from './hooks/useCheckoutState';
import usePaymentMethods from './hooks/usePaymentMethods';
import SummaryScreen, { SummaryFooter } from './screens/SummaryScreen';
import PaymentScreen from './screens/PaymentScreen';
import CompleteScreen from './screens/CompleteScreen';
import AddItemScreen from './screens/AddItemScreen';
import PaymentLinkScreen from './screens/PaymentLinkScreen';
import CardReaderScreen from './screens/CardReaderScreen';
import DiscountScreen from './screens/DiscountScreen';
import CodeScreen from '../discounts/till/CodeScreen';
import { promoFor } from '../discounts/till/tillCode';
import { holdCode, clientKeyOf } from '../discounts/discountStore';
import TipScreen from './screens/TipScreen';
import DepositScreen from './screens/DepositScreen';
import VoucherScreen from './screens/VoucherScreen';
import LoyaltyScreen from './screens/LoyaltyScreen';
import useStripeTerminal from './hooks/useStripeTerminal';
import useTapToPayTerminal from './hooks/useTapToPayTerminal';
import TapToPayScreen from './screens/TapToPayScreen';
import { paymentActions } from '../../_actions';
import { voucherActions } from '../../_actions/voucher.actions';
import { bookingService } from '../../_services/booking.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { receiptService } from '../../_services/receipt.service';
import { marketingBackend } from '../marketing-v2/services/marketingBackend';
import {
  buildServicesToBook,
  effectiveUnitPriceAfterCartDiscount,
  subtotalAfterItemDiscounts,
} from './utils/checkoutCalculations';
import toast from '../shared/Toast';
import { startPaymentIntent, paymentStartError } from './utils/paymentStartError';
import { isIntroActive, isDemoBookingId, introCheckoutBooking, tellIntro } from '../onboarding/bailey-intro/introStage';
import './CheckoutPanel.css';

// Bailey's welcome: payment methods for the practice checkout, matching what
// a new salon would normally see.
const PRACTICE_METHODS = { beuCardReaders: true, tapToPay: false, paymentLink: true, klarna: false, clearPay: false, vouchers: true, cash: true, card: true, other: true };

/**
 * CheckoutPanel
 * SlidePanel-based checkout that opens from the diary.
 * Uses custom layout mode (no title prop) for full control.
 *
 * Props:
 *   bookingId    (string)   - The booking to check out
 *   onClose      (function) - Called when panel closes
 *   onComplete   (function) - Called when checkout finishes (refreshes diary)
 *   dispatch     (function) - Redux dispatch for booking actions
 *   history      (object)   - React Router history (for rebook flow)
 *   currencySymbol (string) - e.g. '£'
 */
const CheckoutPanel = ({ bookingId, onClose, onComplete, onRebook, dispatch, history, currencySymbol = '£' }) => {
  console.log('[CheckoutPanel] tips/discount UI feature branch');
  const [state, actions] = useCheckoutState();
  const terminal = useStripeTerminal();
  const tapToPay = useTapToPayTerminal();
  const panelRef = useRef(null);
  const bookingLoadedRef = useRef(false);
  const checkoutSavedRef = useRef(false);
  // A practice booking from Bailey's welcome: loaded from the tour, never
  // saved, and every payment method only looks.
  const practiceRef = useRef(isIntroActive() && isDemoBookingId(bookingId));
  const practice = practiceRef.current;
  useEffect(() => {
    if (!practice) return undefined;
    tellIntro('checkoutOpened');
    return () => tellIntro('checkoutClosed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (practice) tellIntro(state.screen === 'payment' ? 'checkoutPayment' : 'checkoutScreen', { screen: state.screen });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.screen]);

  // Onboarding: finishing any checkout completes the "Take payment" task.
  useEffect(() => {
    if (state.isComplete) markFirstTask('pay', { bookingId });
  }, [state.isComplete, bookingId]);

  // Sync current checkout items to backend (call before electronic payments)
  const syncBookingToBackend = async () => {
    const salonId = state.salonId || localStorage.getItem('salonId');
    const hairdresserId = state.booking?.hairdresserId
      || state.booking?.hairdresserInfo?._id
      || state.lineItems[0]?.staffId
      || localStorage.getItem('userId');
    const clientId = state.client?._id || state.client?.userId || null;
    // Bake in both per-item and cart-level discounts so the booking record
    // stores the actual sold-at price for each line item (drives commission
    // calc + cashup downstream).
    const denom = subtotalAfterItemDiscounts(state.lineItems);
    const servicesBooked = [];
    state.lineItems.filter(item => item.type === 'service').forEach(item => {
      const qty = item.quantity || 1;
      const servicePrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denom);
      for (let i = 0; i < qty; i++) {
        servicesBooked.push({
          serviceId: item.serviceId || item.id,
          servicePrice,
          serviceTime: item.serviceTime || 0,
          depositPrice: item.depositPrice || 0,
        });
      }
    });
    const products = [];
    state.lineItems.filter(item => item.type === 'product').forEach(item => {
      const qty = item.quantity || 1;
      const retailPrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denom);
      for (let i = 0; i < qty; i++) {
        products.push({
          _id: item.serviceId || item.id,
          productName: item.name,
          // Which variant left the shelf. Without it the backend cannot move
          // the right stock and, rightly refusing to guess, moves none.
          // NB this loop already expands a qty-N line into N entries of one,
          // so it must NOT also send a quantity — that would multiply twice.
          variantId: item.variantId || undefined,
          variantLabel: item.variantLabel || undefined,
          retailPrice,
          stockPrice: item.stockPrice || 0,
          commissionEnable: item.commissionEnable || 0,
        });
      }
    });
    const vouchers = [];
    state.lineItems.filter(item => item.type === 'voucher').forEach(item => {
      const qty = item.quantity || 1;
      const voucherPrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denom);
      for (let i = 0; i < qty; i++) {
        vouchers.push({
          _id: item.serviceId || item.id,
          voucherPrice,                                  // collected (promo) price
          faceValue: item.faceValue != null ? item.faceValue : voucherPrice, // redeemable value
          voucherCode: item.voucherCode || '',
        });
      }
    });
    // Merge existingTransactions (deposits + prior payments hydrated from the
    // booking) with new session transactions. Without this merge, /booking/
    // checkout-edit overwrites booking.transaction[] and any deposit row gets
    // dropped — see issue with cash deposits not surviving final checkout.
    // Tag rows with isDeposit + pgObjectType so the BE can populate legacy
    // booking-level fields and the breakdown UI can render a separate
    // 'Deposit' line for any payment type.
    const mergedTransactions = [
      ...state.existingTransactions.map((t) => ({
        paymentType: t.paymentType,
        amount: t.amount,
        isDeposit: !!t.isDeposit,
        pgObjectType: t.isDeposit ? 'deposit' : 'charge',
        // Preserve the original capture time so deposits don't collapse
        // onto the final-checkout timestamp when the BE serialises this.
        ...(t.createdAt ? { createdAt: t.createdAt } : {}),
        // Keep the gift-card linkage on voucher rows (paymentType 11) so the
        // redemption stays traceable after checkout-edit rewrites the array.
        ...(t.voucherInstanceId ? { voucherInstanceId: t.voucherInstanceId } : {}),
        ...(t.voucherCode ? { voucherCode: t.voucherCode } : {}),
        ...(t.catalogId ? { catalogId: t.catalogId } : {}),
      })),
      ...state.transactions.map((t) => ({
        paymentType: t.paymentType,
        amount: t.amount,
        isDeposit: false,
        pgObjectType: 'charge',
        // Stamp new session payments at this moment, not whenever the BE
        // gets around to handling the request.
        createdAt: t.createdAt || new Date().toISOString(),
        // Keep the scheme linkage on loyalty comp rows (paymentType 16) so
        // checkoutEdit can validate/enrich them and commitRedemption can consume.
        ...(t.loyaltySchemeId ? { loyaltySchemeId: t.loyaltySchemeId } : {}),
      })),
    ];
    await bookingService.editCheckoutBooking({
      bookingId: state.bookingId,
      hairdresserId,
      salonId,
      userId: clientId || hairdresserId,
      servicesBooked,
      products,
      vouchers,
      transaction: JSON.stringify(mergedTransactions),
      totalAmount: parseFloat(state.totalDue).toFixed(2),
      isCheckout: 0,
      selectedPaymentType: 5,
    });
  };

  const pmHook = usePaymentMethods({ state, actions, dispatch, syncBookingToBackend });
  // Named in the message when the server refuses a card payment for this
  // stylist (e.g. their Stripe payouts aren't set up yet).
  const paymentStaffName = (state.lineItems || []).find(i => i.type === 'service')?.staffName
    || state.booking?.hairdresser?.name || state.booking?.hairdresserInfo?.name || '';

  // Load card readers + card on file info. Depends on bookingId so it re-fires
  // once the booking prop lands — the backend needs the bookingId to look up
  // the stored payment method and decide whether card-on-file is available.
  useEffect(() => {
    if (practice) return;
    if (dispatch && bookingId) pmHook.loadCardReaders(bookingId);
  }, [bookingId]);

  // Load booking data on mount (once only — prevents Redux re-renders from wiping local state)
  useEffect(() => {
    if (!bookingId || bookingLoadedRef.current) return;
    bookingLoadedRef.current = true;
    actions.setLoading(true);

    if (practice) {
      const demo = introCheckoutBooking(bookingId);
      if (!demo) { actions.setError('Booking not found'); return; }
      actions.loadBooking(demo);
      actions.setPaymentMethods(PRACTICE_METHODS);
      return;
    }

    bookingService.getBookingByIdV2(bookingId)
      .then(response => {
        const rawData = response?.data?.data || response?.data?.responseData;
        // V2 API may return bookings array or single object
        const bookingData = Array.isArray(rawData?.bookings) ? rawData.bookings[0] : rawData;
        if (!bookingData) {
          actions.setError('Booking not found');
          return;
        }
        actions.loadBooking(bookingData);
      })
      .catch(() => {
        actions.setError('Failed to load booking');
      });
  }, [bookingId]);

  // Load payment methods for the booking's stylist (not the logged-in user).
  // Re-fires when the booking loads so we hit the right hairdresser, and the
  // response is unwrapped one level deeper because the API returns
  // { paymentMethods: { cash: bool, ... } } not the flags directly.
  const bookingHairdresserId = state.booking?.hairdresserId
    || state.booking?.hairdresserInfo?._id;
  useEffect(() => {
    if (!bookingHairdresserId || practice) return;
    hairdresserService.getPaymentMethods({ hairdresserId: bookingHairdresserId })
      .then(response => {
        const data = response?.data?.data || response?.data?.responseData || {};
        const methods = data.paymentMethods || data;
        actions.setPaymentMethods(methods);
      })
      .catch(() => {});
  }, [bookingHairdresserId]);

  // Load ALL of this client's loyalty cards once the booking's client is known.
  // Not just redeemable ones: the till shows the reward button either way, with
  // stamp progress when there is nothing to redeem yet. LoyaltyScreen filters
  // down to redeemable cards itself.
  const checkoutClientId = state.client?._id || state.client?.userId;
  useEffect(() => {
    if (!checkoutClientId || practice) { actions.setLoyaltyRewards([]); return; }
    marketingBackend.loyaltyClientCards(checkoutClientId)
      .then((res) => actions.setLoyaltyRewards((res && res.cards) || []))
      .catch(() => actions.setLoyaltyRewards([]));
  }, [checkoutClientId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      actions.reset();
    };
  }, []);

  // Save checkout to backend when complete
  useEffect(() => {
    if (!state.isComplete || !state.bookingId || practice) return;
    if (checkoutSavedRef.current) return;
    checkoutSavedRef.current = true;

    const saveCheckout = async () => {
      try {
        const salonId = state.salonId || localStorage.getItem('salonId');
        const hairdresserId = state.booking?.hairdresserId
          || state.booking?.hairdresserInfo?._id
          || state.lineItems[0]?.staffId
          || localStorage.getItem('userId');
        const clientId = state.client?._id || state.client?.userId || null;

        // Build services / products / vouchers for V2 API (expand quantities).
        // Use the same cart-discount-aware unit price helper so both the
        // backend's commission calc and the booking record reflect the
        // actual sold-at price for every line item.
        const denomFinal = subtotalAfterItemDiscounts(state.lineItems);

        const servicesBooked = [];
        state.lineItems.filter(item => item.type === 'service').forEach(item => {
          const qty = item.quantity || 1;
          const servicePrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denomFinal);
          for (let i = 0; i < qty; i++) {
            servicesBooked.push({
              serviceId: item.serviceId || item.id,
              servicePrice,
              serviceTime: item.serviceTime || 0,
              depositPrice: item.depositPrice || 0,
            });
          }
        });

        const products = [];
        state.lineItems.filter(item => item.type === 'product').forEach(item => {
          const qty = item.quantity || 1;
          const retailPrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denomFinal);
          for (let i = 0; i < qty; i++) {
            products.push({
              _id: item.serviceId || item.id,
              productName: item.name,
              retailPrice,
              stockPrice: item.stockPrice || 0,
              commissionEnable: item.commissionEnable || 0,
            });
          }
        });

        const vouchers = [];
        state.lineItems.filter(item => item.type === 'voucher').forEach(item => {
          const qty = item.quantity || 1;
          const voucherPrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denomFinal);
          for (let i = 0; i < qty; i++) {
            vouchers.push({
              _id: item.serviceId || item.id,
              voucherPrice,                                  // collected (promo) price
              faceValue: item.faceValue != null ? item.faceValue : voucherPrice, // redeemable value
              voucherCode: item.voucherCode || '',
            });
          }
        });

        // Build transaction array (JSON string as V2 backend expects).
        // Include existingTransactions (deposits + prior payments hydrated from
        // the booking) so checkoutEdit doesn't clobber them — without this, a
        // cash deposit taken earlier in the same flow gets overwritten by the
        // final-checkout save. Tag each row so the BE can populate legacy
        // booking-level fields and the breakdown UI knows which is the deposit.
        const transaction = JSON.stringify([
          ...state.existingTransactions.map((t) => ({
            paymentType: t.paymentType,
            amount: t.amount,
            isDeposit: !!t.isDeposit,
            pgObjectType: t.isDeposit ? 'deposit' : 'charge',
            // Preserve original capture time on deposits so they don't
            // collapse onto the final-checkout timestamp.
            ...(t.createdAt ? { createdAt: t.createdAt } : {}),
            // Keep the gift-card linkage on voucher rows (paymentType 11).
            ...(t.voucherInstanceId ? { voucherInstanceId: t.voucherInstanceId } : {}),
            ...(t.voucherCode ? { voucherCode: t.voucherCode } : {}),
            ...(t.catalogId ? { catalogId: t.catalogId } : {}),
          })),
          ...state.transactions.map((t) => ({
            paymentType: t.paymentType,
            amount: t.amount,
            isDeposit: false,
            pgObjectType: 'charge',
            // Stamp now for new session payments (BE would default to
            // its own new Date() otherwise; this is more accurate to
            // when the customer actually completed the action).
            createdAt: t.createdAt || new Date().toISOString(),
            ...(t.voucherId ? { voucherId: t.voucherId, voucherCode: t.voucherCode } : {}),
            // Keep the scheme linkage on loyalty comp rows (paymentType 16).
            ...(t.loyaltySchemeId ? { loyaltySchemeId: t.loyaltySchemeId } : {}),
          })),
        ]);

        // Determine dominant payment type for the checkout
        const primaryTransaction = state.transactions.reduce((a, b) => b.amount > a.amount ? b : a, state.transactions[0] || {});
        const selectedPaymentType = primaryTransaction?.paymentType || 3;

        // Only hit editCheckoutBooking when we have new session transactions
        // to persist. Re-opening an already-paid booking would otherwise
        // clobber the server-side transaction array with an empty list.
        if (state.transactions.length > 0) {
          await bookingService.editCheckoutBooking({
            bookingId: state.bookingId,
            hairdresserId,
            salonId,
            userId: clientId || hairdresserId,
            servicesBooked,
            products,
            vouchers,
            transaction,
            totalAmount: parseFloat(state.totalDue).toFixed(2),
            isCheckout: 1,
            selectedPaymentType,
            tipAmount: state.tipAmount || 0,
            discountAmount: state.discountTotal || 0,
            // A discount code, once the server checks codes (it refuses fields
            // it doesn't know): which code, and its saving on each line
            ...(state.promo && state.promo.serverChecked ? { promo: promoFor(state.promo, state.lineItems) } : {}),
          });
        }

        // The code's use, held on this booking (the device stand-in needs
        // telling; the server takes it from `promo`)
        if (state.promo) {
          holdCode({
            salonId,
            code: state.promo.code,
            bookingId: state.bookingId,
            clientKey: clientKeyOf({ phone: state.client && (state.client.phoneNumber || state.client.phone), userId: state.client && state.client._id }),
            amount: state.promo.amount,
            total: state.totalDue,
            channel: 'salon',
          }).catch(() => {});
        }

        // Safety net: never flip bookingStatus to 4 if there's an outstanding
        // balance. markComplete is already guarded, but this is the final
        // backstop in case a future code path dispatches MARK_COMPLETE directly.
        if ((state.balanceRemaining || 0) > 0.01) {
          toast.error(`Can't complete. £${(state.balanceRemaining || 0).toFixed(2)} outstanding.`);
          return;
        }

        // Mark booking as complete via V2
        await bookingService.bookingComplete({
          bookingId: state.bookingId,
          bookingStatus: 4,
          salonId,
          tipAmount: state.tipAmount,
        });

        // Link voucher redemptions to this booking
        const voucherTransactions = state.transactions.filter(t => t.paymentType === 11 && t.voucherId);
        for (const vt of voucherTransactions) {
          try {
            await dispatch(voucherActions.updateVoucherRedemption(
              vt.voucherId,
              vt.redemptionIndex || 0,
              state.bookingId
            ));
          } catch (e) {
            // Non-critical: voucher was already redeemed, linking is for records
          }
        }

        // Send receipt email if client has an email. The endpoint takes exactly
        // { bookingId, email }: it requires `email` (it does not derive it from
        // the booking) and rejects any extra key, so passing salonId fails
        // validation. Same shape as the other receipt callers.
        if (state.client?.email) {
          try {
            await receiptService.sendReceiptEmail({
              bookingId: state.bookingId,
              email: state.client.email,
            });
          } catch (e) {
            // Non-critical: receipt email failure shouldn't block checkout
          }
        }
      } catch (e) {
        toast.error('Failed to save checkout. The payment was processed but the booking may need manual update.');
        actions.setError('Checkout save failed. Please contact support if the booking appears incorrect.');
      }
    };

    saveCheckout();
  }, [state.isComplete]);

  const handleClose = () => {
    if (state.isProcessingPayment) return;
    if (state.isComplete && onComplete) onComplete();
    if (onClose) onClose();
  };

  const renderScreen = () => {
    if (state.isLoading) {
      return (
        <div className="co-loading">
          <div className="co-spinner" />
        </div>
      );
    }

    if (state.error) {
      return (
        <div className="co-error">
          <p>{state.error}</p>
          <button className="co-btn co-btn--secondary" onClick={handleClose}>Close</button>
        </div>
      );
    }

    // Roles & permissions: taking payment for someone else's booking needs
    // `pos`, discounting it `discounts` (own bookings always allowed).
    const saleStaff = [state.booking?.hairdresserId || state.booking?.hairdresserInfo?._id].concat((state.lineItems || []).map((i) => i.staffId));
    const blocked = (state.screen === 'payment' && !canOnSale('pos', saleStaff)) || (state.screen === 'discount' && !canOnSale('discounts', saleStaff));
    if (blocked) {
      return (
        <div className="co-error">
          <p>{state.screen === 'payment' ? 'Your role doesn\'t let you take payment for someone else\'s booking.' : 'Your role doesn\'t let you discount someone else\'s booking.'} Ask the salon owner if you need to.</p>
          <button className="co-btn co-btn--secondary" onClick={() => actions.setScreen('summary')}>Back</button>
        </div>
      );
    }

    switch (state.screen) {
      case 'payment':
        return (
          <div className="co-screen-slide-in" key="payment">
            <PaymentScreen
              state={state}
              actions={actions}
              dispatch={dispatch}
              onBack={() => actions.setScreen('summary')}
              paymentMethods={pmHook}
              tapToPay={tapToPay}
              practice={practice}
            />
          </div>
        );

      case 'tip':
        return (
          <div className="co-screen-slide-in" key="tip">
            <TipScreen
              state={state}
              actions={actions}
              onBack={() => actions.setScreen('summary')}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'paymentLink':
        return (
          <div className="co-screen-slide-in" key="paymentLink">
            <PaymentLinkScreen
              paymentLinkData={pmHook.paymentLinkData}
              paymentType={pmHook.paymentLinkType}
              onConfirm={(booking) => {
                pmHook.handlePaymentLinkComplete(booking);
              }}
              onClose={() => {
                pmHook.closePaymentLinkModal();
                actions.setScreen('payment');
              }}
              onCheckStatus={pmHook.checkPaymentLinkStatus}
              currencySymbol={state.currencySymbol || currencySymbol}
              clientEmail={state.client?.email || ''}
              clientName={state.client?.name || ''}
            />
          </div>
        );

      case 'deposit':
        return (
          <div className="co-screen-slide-in" key="deposit">
            <DepositScreen
              state={state}
              actions={actions}
              dispatch={dispatch}
              terminal={terminal}
              tapToPay={tapToPay}
              paymentMethods={pmHook}
              onBack={() => actions.setScreen('summary')}
              onDepositComplete={() => {
                // Close the panel and refresh diary
                if (onComplete) onComplete();
                if (onClose) onClose();
              }}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'code':
        return (
          <div className="co-screen-slide-in" key="code">
            <CodeScreen
              items={state.lineItems}
              promo={state.promo}
              client={state.client}
              salonId={state.salonId}
              subtotal={state.subtotal}
              currencySymbol={state.currencySymbol || currencySymbol}
              onApply={actions.applyPromo}
              onRemove={actions.removePromo}
              onBack={() => actions.setScreen('summary')}
            />
          </div>
        );

      case 'discount':
        return (
          <div className="co-screen-slide-in" key="discount">
            <DiscountScreen
              state={state}
              actions={actions}
              onBack={() => actions.setScreen('summary')}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'cardReader': {
        // Two modes: regular session payment OR a card-reader deposit (when
        // pendingDepositAmount is set by DepositScreen). Branches on that.
        const isDepositMode = state.pendingDepositAmount != null;
        const cardReaderAmount = isDepositMode
          ? state.pendingDepositAmount
          : (state.amountMode === 'balance' ? state.balanceRemaining : parseFloat(state.customAmount) || 0);
        return (
          <div className="co-screen-slide-in" key="cardReader">
            <CardReaderScreen
              cardReaders={pmHook.cardReaderList}
              terminal={terminal}
              amount={cardReaderAmount}
              dispatch={dispatch}
              onClose={handleClose}
              onReadersRefresh={() => pmHook.loadCardReaders(bookingId)}
              onBack={() => {
                if (terminal.isProcessing) return;
                if (isDepositMode) {
                  actions.setPendingDeposit(null);
                  actions.setScreen('deposit');
                } else {
                  actions.setScreen('payment');
                }
              }}
              onPaymentComplete={async () => {
                const amount = cardReaderAmount;

                if (isDepositMode) {
                  // Card-reader deposit flow: create paymentIntent, process on
                  // reader, then surface as a deposit row on the booking.
                  const servicesToBook = buildServicesToBook(state.lineItems, state.cartDiscount);
                  const response = await startPaymentIntent(() => dispatch(paymentActions.paymentIntents({
                    bookingId: state.bookingId,
                    servicesToBook,
                    productIds: [],
                    voucherIds: [],
                    amount,
                    selectedPaymentType: 5,
                  })), paymentStaffName);
                  const clientSecret = response.data.responseData?.result?.client_secret;
                  const result = await terminal.processPayment({
                    clientSecret,
                    paymentIntentId: response.data.responseData?.result?.id,
                    bookingId: state.bookingId,
                    servicesToBook,
                    voucherIds: [],
                    isDeposit: true,
                    onCapture: (params) => dispatch(paymentActions.paymentIntentsCapture(params)).then(r => r),
                  });
                  if (result?.success) {
                    if (actions.addExistingTransaction) {
                      actions.addExistingTransaction({
                        paymentType: 5,
                        amount: result.amount || amount,
                        label: 'Card Reader Deposit',
                        isDeposit: true,
                        createdAt: new Date().toISOString(),
                      });
                    }
                    actions.setPendingDeposit(null);
                    if (onComplete) onComplete();
                    if (onClose) onClose();
                  }
                  return;
                }

                // Regular session payment (no pending deposit)
                const params = pmHook.buildPaymentParams ? pmHook.buildPaymentParams(amount) : {};

                // Save current items to backend before creating payment intent
                try {
                  await syncBookingToBackend();
                } catch (e) {
                  throw new Error(paymentStartError(e, paymentStaffName));
                }

                // Create payment intent on backend
                const response = await startPaymentIntent(() => dispatch(paymentActions.paymentIntents({
                  ...params,
                  selectedPaymentType: 5,
                })), paymentStaffName);
                if (response?.data?.statusCode === 1) {
                  const clientSecret = response.data.responseData?.result?.client_secret;
                  const result = await terminal.processPayment({
                    clientSecret,
                    paymentIntentId: response.data.responseData?.result?.id,
                    bookingId: state.bookingId,
                    servicesToBook: buildServicesToBook(state.lineItems, state.cartDiscount),
                    voucherIds: [],
                    onCapture: (params) => dispatch(paymentActions.paymentIntentsCapture(params)).then(r => r),
                  });
                  if (result?.success) {
                    actions.addTransaction({
                      paymentType: 5,
                      amount: result.amount || amount,
                      label: 'Card Reader',
                    });
                    if (result.tipAmount > 0) {
                      actions.setTip({ amount: result.tipAmount, type: 'card_reader' });
                    }
                    // Payment already captured electronically — auto-complete
                    setTimeout(() => actions.markComplete(), 100);
                  }
                }
              }}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );
      }

      case 'tapToPay': {
        // Mirrors the cardReader regular-session payment flow but uses the
        // native iOS Stripe Terminal SDK Tap-to-Pay reader. The native
        // sheet handles the actual "tap your card" UI; this screen just
        // shows connecting / processing state and a cancel button.
        const isDepositMode = state.pendingDepositAmount != null;
        const tapAmount = isDepositMode
          ? state.pendingDepositAmount
          : (state.amountMode === 'balance' ? state.balanceRemaining : parseFloat(state.customAmount) || 0);
        const stripeLocationId = state.booking?.hairdresser?.stripeLocationId
          || state.booking?.hairdresserInfo?.stripeLocationId
          || localStorage.getItem('stripeLocationId');
        return (
          <div className="co-screen-slide-in" key="tapToPay">
            <TapToPayScreen
              terminal={tapToPay}
              amount={tapAmount}
              locationId={stripeLocationId}
              currencySymbol={state.currencySymbol || currencySymbol}
              onBack={() => {
                if (tapToPay.isProcessing) return;
                if (isDepositMode) {
                  actions.setPendingDeposit(null);
                  actions.setScreen('deposit');
                } else {
                  actions.setScreen('payment');
                }
              }}
              onPaymentComplete={async () => {
                const amount = tapAmount;
                const servicesToBook = buildServicesToBook(state.lineItems, state.cartDiscount);

                if (isDepositMode) {
                  // Tap-to-Pay deposit flow
                  const response = await startPaymentIntent(() => dispatch(paymentActions.paymentIntents({
                    bookingId: state.bookingId,
                    servicesToBook,
                    productIds: [],
                    voucherIds: [],
                    amount,
                    selectedPaymentType: 15,
                  })), paymentStaffName);
                  const clientSecret = response.data.responseData?.result?.client_secret;
                  const result = await tapToPay.processPayment({
                    clientSecret,
                    paymentIntentId: response.data.responseData?.result?.id,
                    bookingId: state.bookingId,
                    servicesToBook,
                    voucherIds: [],
                    onCapture: (params) => dispatch(paymentActions.paymentIntentsCapture({ ...params, isDeposit: 1 })).then(r => r),
                  });
                  if (result?.success) {
                    if (actions.addExistingTransaction) {
                      actions.addExistingTransaction({
                        paymentType: 15,
                        amount: result.amount || amount,
                        label: 'Tap to Pay Deposit',
                        isDeposit: true,
                      });
                    }
                    actions.setPendingDeposit(null);
                    if (onComplete) onComplete();
                    if (onClose) onClose();
                  }
                  return;
                }

                // Regular session Tap-to-Pay payment
                try {
                  await syncBookingToBackend();
                } catch (e) {
                  throw new Error(paymentStartError(e, paymentStaffName));
                }
                const response = await startPaymentIntent(() => dispatch(paymentActions.paymentIntents({
                  bookingId: state.bookingId,
                  servicesToBook,
                  productIds: [],
                  voucherIds: [],
                  amount,
                  selectedPaymentType: 15,
                })), paymentStaffName);
                if (response?.data?.statusCode === 1) {
                  const clientSecret = response.data.responseData?.result?.client_secret;
                  const result = await tapToPay.processPayment({
                    clientSecret,
                    paymentIntentId: response.data.responseData?.result?.id,
                    bookingId: state.bookingId,
                    servicesToBook,
                    voucherIds: [],
                    onCapture: (params) => dispatch(paymentActions.paymentIntentsCapture(params)).then(r => r),
                  });
                  if (result?.success) {
                    actions.addTransaction({
                      paymentType: 15,
                      amount: result.amount || amount,
                      label: 'Tap to Pay',
                    });
                    if (result.tipAmount > 0) {
                      actions.setTip({ amount: result.tipAmount, type: 'tap_to_pay' });
                    }
                    setTimeout(() => actions.markComplete(), 100);
                  }
                }
              }}
            />
          </div>
        );
      }

      case 'voucher':
        return (
          <div className="co-screen-slide-in" key="voucher">
            <VoucherScreen
              state={state}
              actions={actions}
              dispatch={dispatch}
              onBack={() => actions.setScreen('payment')}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'loyalty':
        return (
          <div className="co-screen-slide-in" key="loyalty">
            <LoyaltyScreen
              state={state}
              actions={actions}
              onBack={() => actions.setScreen('payment')}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'addItem':
        return (
          <div className="co-screen-slide-in" key="addItem">
            <AddItemScreen
              state={state}
              actions={actions}
              dispatch={dispatch}
              onBack={() => actions.setScreen('summary')}
              currencySymbol={state.currencySymbol || currencySymbol}
              practice={practice}
            />
          </div>
        );

      case 'complete':
        return (
          <div className="co-screen-slide-in" key="complete">
            <CompleteScreen
              state={state}
              onDone={handleClose}
              dispatch={dispatch}
              currencySymbol={state.currencySymbol || currencySymbol}
              onRebook={() => {
                if (onRebook) {
                  const services = state.lineItems
                    .filter(item => item.type === 'service')
                    .map(item => ({
                      service: { serviceId: item.serviceId, _id: item.serviceId, serviceName: item.name, servicePrice: item.originalPrice, diaryColour: item.diaryColour },
                      serviceId: item.serviceId,
                      serviceName: item.name,
                      servicePrice: item.originalPrice,
                      serviceTime: item.serviceTime || 30,
                      processingTime: 0,
                      diaryColour: item.diaryColour,
                      hairdresserId: item.staffId,
                    }));
                  const staffId = state.lineItems[0]?.staffId;
                  const client = state.client ? { _id: state.client._id || state.client.userId, name: state.client.name || '' } : null;
                  onRebook({ staffId, services, client });
                } else {
                  handleClose();
                }
              }}
              currencySymbol={state.currencySymbol || currencySymbol}
            />
          </div>
        );

      case 'summary':
      default:
        return (
          <SummaryScreen
            state={state}
            actions={actions}
            onCheckout={() => actions.setScreen('payment')}
            onClose={handleClose}
            currencySymbol={state.currencySymbol || currencySymbol}
          />
        );
    }
  };

  // Header with client info
  const clientName = state.client?.name || 'Walk-in';
  const clientInitials = clientName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const hasAvatar = state.client?.localAvatar || state.client?.socialAvatar;
  const avatarSrc = state.client?.localAvatar || state.client?.socialAvatar;

  return (
    <SlidePanel
      ref={panelRef}
      onClose={handleClose}
      className="co-panel"
      width={480}
    >
      <div className="co-layout">
        {/* Custom header */}
        <div className="co-header">
          <div className="co-header-left">
            {hasAvatar ? (
              <img className="co-avatar" src={avatarSrc} alt="" onError={e => { e.target.style.display = 'none'; }} />
            ) : (
              <div className="co-avatar co-avatar--initials">{clientInitials}</div>
            )}
            <div>
              <div className="co-client-name">{clientName}</div>
              <div className="co-client-sub">Checkout</div>
            </div>
          </div>
          <button className="co-close-btn" onClick={handleClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="co-body">
          {renderScreen()}
        </div>

        {/* Fixed footer - only on summary screen */}
        {!state.isLoading && !state.error && state.screen === 'summary' && (
          <SummaryFooter
            state={state}
            actions={actions}
            onCheckout={() => actions.setScreen('payment')}
            onClose={handleClose}
            currencySymbol={state.currencySymbol || currencySymbol}
          />
        )}
      </div>

    </SlidePanel>
  );
};

export default CheckoutPanel;
